import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, Users, Car, Package, Wrench, FileText } from 'lucide-react';
import { DOCUMENT_TYPE_LABELS, type DocumentType } from '@garage/shared';
import { api } from '@/lib/axios';
import { useDebounce } from '@/hooks/useDebounce';

interface SearchResults {
  customers: { id: string; firstName: string; lastName: string; phone: string | null }[];
  vehicles: { id: string; licensePlate: string; make: string; model: string; customerId: string }[];
  parts: { id: string; reference: string; name: string }[];
  repairOrders: { id: string; number: string }[];
  invoices: { id: string; number: string; type: DocumentType }[];
}

interface Item { key: string; icon: typeof Users; label: string; sub: string; link: string }

export function GlobalSearch() {
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const [value, setValue] = useState('');
  const [open, setOpen] = useState(false);
  const q = useDebounce(value, 250);

  const { data } = useQuery({
    queryKey: ['global-search', q],
    queryFn: async () => (await api.get<SearchResults>('/search', { params: { q } })).data,
    enabled: q.trim().length >= 2,
  });

  useEffect(() => {
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const items: Item[] = data
    ? [
        ...data.customers.map((c) => ({ key: `c${c.id}`, icon: Users, label: `${c.lastName} ${c.firstName}`, sub: c.phone ?? 'Client', link: `/clients/${c.id}` })),
        ...data.vehicles.map((v) => ({ key: `v${v.id}`, icon: Car, label: `${v.licensePlate}`, sub: `${v.make} ${v.model}`, link: `/vehicules/${v.id}` })),
        ...data.parts.map((p) => ({ key: `p${p.id}`, icon: Package, label: p.name, sub: p.reference, link: `/pieces` })),
        ...data.repairOrders.map((o) => ({ key: `o${o.id}`, icon: Wrench, label: o.number, sub: 'Ordre de réparation', link: `/reparations/${o.id}` })),
        ...data.invoices.map((i) => ({ key: `i${i.id}`, icon: FileText, label: i.number, sub: DOCUMENT_TYPE_LABELS[i.type], link: `/facturation/${i.id}` })),
      ]
    : [];

  const go = (link: string) => { setOpen(false); setValue(''); navigate(link); };

  return (
    <div ref={ref} className="relative hidden max-w-md flex-1 md:block">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <input
        type="search"
        placeholder="Recherche globale…"
        value={value}
        onChange={(e) => { setValue(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); if (e.key === 'Enter' && items[0]) go(items[0].link); }}
        className="w-full rounded-md border border-input bg-background py-2 pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-ring"
      />

      {open && q.trim().length >= 2 && (
        <div className="absolute left-0 right-0 z-50 mt-2 max-h-96 overflow-y-auto rounded-lg border border-border bg-card shadow-xl">
          {items.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-muted-foreground">Aucun résultat pour « {q} ».</p>
          ) : (
            items.map((it) => (
              <button key={it.key} type="button" onClick={() => go(it.link)}
                className="flex w-full items-center gap-3 border-b border-border px-4 py-2 text-left last:border-0 hover:bg-muted">
                <it.icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium">{it.label}</span>
                  <span className="block truncate text-xs text-muted-foreground">{it.sub}</span>
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
